/**
 * Induction Platform — Frontend Entry Point
 *
 * Uses the shared @isg/ui-kit app shell (accent stripe, sidebar navigation,
 * mobile drawer, toolbar) exactly like the other estate apps. Admin pages
 * live in an admin-only sidebar section rather than a separate shell.
 */

// --- Styles ---
import '@isg/ui-kit/css/tokens.css';
import '@isg/ui-kit/css/reset.css';
import '@isg/ui-kit/css/components.css';
import '@isg/ui-kit/css/layout.css';
import './styles/induction.css';
import './styles/admin.css';

// --- Services & Utils ---
import { router, store, auth, api , checkEnvBanner } from '@isg/ui-kit';
import { applyBranding, loadBranding, initTheme } from '@isg/ui-kit';
import { getIcon, renderNavigation, renderMobileHeader, createSidebarOverlay } from '@isg/ui-kit';
import { t, initI18n } from './i18n/index.js';
import { renderLanguageSwitcher } from './components/language-switcher.js';
import { renderAIAssistant } from './components/ai-assistant-widget.js';

/* SSO: capture handoff from intranet broker */
auth.captureSSOHandoff();

// --- Lazy-loaded pages ---
const pages = {
  // User-facing
  dashboard:  () => import('./pages/dashboard.js'),
  module:     () => import('./pages/module-viewer.js'),
  documents:  () => import('./pages/documents.js'),
  login:      () => import('./pages/login.js'),
  // Admin
  adminDashboard:    () => import('./pages/admin-dashboard.js'),
  adminModules:      () => import('./pages/admin-modules.js'),
  adminModuleEditor: () => import('./pages/admin-module-editor.js'),
  adminUsers:        () => import('./pages/admin-users.js'),
  adminReports:      () => import('./pages/admin-reports.js'),
  adminMedia:        () => import('./pages/admin-media.js'),
};

// Induction admin tiers (broader than ui-kit's auth.isAdmin, which only
// recognises the platform 'admin' role — hr_admin/super_admin/bu_admin are
// resolved into user.role by the /api/user/profile override).
// i18n-ignore: role slugs for an access check, never displayed
const ADMIN_ROLES = ['admin', 'bu_admin', 'hr_admin', 'super_admin'];

function isInductionAdmin() {
  const user = store.get('user') || {};
  return ADMIN_ROLES.includes(user.role || '');
}

// ── Sidebar nav config (admin section only present for admins) ────────
function buildNavConfig() {
  const cfg = [
    {
      section: null,
      items: [
        { path: '/', label: t('nav.induction'), icon: 'graduation-cap' },
        { path: '/documents', label: t('nav.documents'), icon: 'file-text' },
      ],
    },
  ];
  if (isInductionAdmin()) {
    cfg.push({
      section: t('nav.admin'),
      items: [
        { path: '/admin', label: t('nav.dashboard'), icon: 'layout-dashboard' },
        { path: '/admin/modules', label: t('nav.modules'), icon: 'book-open' },
        { path: '/admin/users', label: t('nav.users'), icon: 'users' },
        { path: '/admin/media', label: t('nav.media'), icon: 'folder' },
        { path: '/admin/reports', label: t('nav.reports'), icon: 'bar-chart-2' },
      ],
    });
  }
  return cfg;
}

// ── Render a page module ────────────────────────────────────────────
async function renderPage(loader, params = {}) {
  const main = document.getElementById('main-content');
  if (!main) return;

  main.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:50vh">
      <div class="spinner"></div>
    </div>
  `;

  try {
    const module = await loader();
    main.innerHTML = '';
    await module.render(main, params);
  } catch (err) {
    console.error('Page render error:', err);
    main.innerHTML = `
      <div class="main-content-inner">
        <div class="empty-state">
          <p style="color: var(--red-600); font-weight: 500;">${t('common.error')}</p>
          <p>${err.message || t('shell.loadFailed')}</p>
          <button class="btn btn-primary" style="margin-top:1rem;" onclick="location.reload()">${t('shell.reload')}</button>
        </div>
      </div>
    `;
  }
}

// ── Preview Launcher (toolbar, admins only) ───────────────────────────
function renderPreviewLauncher(mount) {
  let open = false;
  let companies = [];
  let departments = [];
  let selectedCompany = '';
  let selectedDepartment = '';

  function render() {
    mount.innerHTML = `
      <div style="position:relative;">
        <button id="preview-toggle" style="display:inline-flex;align-items:center;gap:0.375rem;font-size:0.875rem;color:var(--text-secondary);padding:0.375rem 0.75rem;border-radius:0.5rem;border:1px solid var(--border-primary);background:var(--surface-primary);cursor:pointer;transition:all 0.15s;">
          ${getIcon('eye', 16)}
          ${t('shell.preview.button')}
        </button>
        ${open ? `
          <div id="preview-dropdown" style="position:absolute;right:0;top:100%;margin-top:0.5rem;background:var(--surface-elevated);border:1px solid var(--border-primary);border-radius:0.75rem;box-shadow:var(--shadow-xl);width:18rem;z-index:1100;overflow:hidden;">
            <div style="padding:0.75rem 1rem;border-bottom:1px solid var(--border-secondary);">
              <p style="font-size:0.875rem;font-weight:600;color:var(--text-primary);">${t('shell.preview.title')}</p>
              <p style="font-size:0.75rem;color:var(--gray-400);margin-top:0.125rem;">${t('shell.preview.subtitle')}</p>
            </div>
            <div style="padding:0.75rem 1rem;display:flex;flex-direction:column;gap:0.5rem;">
              <div>
                <label style="display:block;font-size:0.75rem;font-weight:500;color:var(--text-secondary);margin-bottom:0.25rem;">${t('admin.users.company')}</label>
                <select id="preview-company" class="form-input" style="width:100%;padding:0.375rem 0.5rem;font-size:0.8125rem;">
                  <option value="global">${t('shell.preview.global')}</option>
                  ${companies.map(c => `<option value="${c.slug || c.id}" ${selectedCompany === (c.slug || String(c.id)) ? 'selected' : ''}>${c.name} (${c.country})</option>`).join('')}
                </select>
              </div>
              <div>
                <label style="display:block;font-size:0.75rem;font-weight:500;color:var(--text-secondary);margin-bottom:0.25rem;">${t('shell.preview.department')} <span style="color:var(--gray-400);">${t('shell.preview.optional')}</span></label>
                <select id="preview-dept" class="form-input" style="width:100%;padding:0.375rem 0.5rem;font-size:0.8125rem;">
                  <option value="">${t('shell.preview.anyDepartment')}</option>
                  ${departments.map(d => `<option value="${d.name}" ${selectedDepartment === d.name ? 'selected' : ''}>${d.name}</option>`).join('')}
                </select>
              </div>
              <button id="preview-launch" class="btn btn-primary btn-sm" style="width:100%;margin-top:0.25rem;">
                ${getIcon('eye', 14)} ${t('shell.preview.launch')}
              </button>
            </div>
          </div>
        ` : ''}
      </div>
    `;

    mount.querySelector('#preview-toggle')?.addEventListener('click', async () => {
      open = !open;
      if (open && companies.length === 0) {
        const [cData, dData] = await Promise.all([
          api.get('/admin/companies').catch(() => []),
          api.get('/admin/departments').catch(() => []),
        ]);
        companies = Array.isArray(cData) ? cData : [];
        departments = Array.isArray(dData) ? dData : [];
      }
      render();
    });

    mount.querySelector('#preview-company')?.addEventListener('change', (e) => {
      selectedCompany = e.target.value;
    });
    mount.querySelector('#preview-dept')?.addEventListener('change', (e) => {
      selectedDepartment = e.target.value;
    });

    mount.querySelector('#preview-launch')?.addEventListener('click', () => {
      const companyVal = mount.querySelector('#preview-company')?.value || 'global';
      const deptVal = mount.querySelector('#preview-dept')?.value || '';
      let url = `/?previewCompanyId=${encodeURIComponent(companyVal)}`;
      if (deptVal) url += `&previewDepartment=${encodeURIComponent(deptVal)}`;
      window.location.href = url;
    });

    // Close on click outside
    document.addEventListener('click', (e) => {
      if (open && !mount.contains(e.target)) {
        open = false;
        render();
      }
    }, { once: true });
  }

  render();
}

// ── Shared app shell (ui-kit sidebar + toolbar) ──────────────────────
function renderAppShell() {
  const app = document.getElementById('app');
  const isAdmin = isInductionAdmin();
  app.innerHTML = '';

  // Accent stripe at top
  const stripe = document.createElement('div');
  stripe.className = 'accent-stripe';
  app.appendChild(stripe);

  const layout = document.createElement('div');
  layout.className = 'app-layout';

  const mobileHeader = renderMobileHeader({ appTitle: t('app.title') });
  const overlay = createSidebarOverlay();

  const sidebar = renderNavigation({
    navConfig: buildNavConfig(),
    appTitle: t('app.shortTitle'),
    fallbackInitial: 'ISG',
    appSlug: 'induction',
  });

  // Main wrapper with toolbar
  const mainWrapper = document.createElement('div');
  mainWrapper.style.cssText = 'display:flex;flex-direction:column;flex:1;min-width:0;';

  // Admin preview mode banner (above the toolbar)
  const urlParams = new URLSearchParams(window.location.search);
  const previewCompanyId = urlParams.get('previewCompanyId');
  const previewDepartment = urlParams.get('previewDepartment');
  if (previewCompanyId && isAdmin) {
    // Both values come off the URL: escape before they reach innerHTML.
    const escHtml = (v) => String(v).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
    const companyLabel = previewCompanyId === 'global' ? t('shell.preview.global') : escHtml(previewCompanyId);
    const banner = document.createElement('div');
    banner.className = 'preview-banner';
    banner.id = 'preview-banner';
    banner.innerHTML = `
      ${getIcon('eye', 16)}
      <span>${previewDepartment
        ? t('shell.preview.bannerWithDepartment', {
          company: `<strong id="preview-company-name">${companyLabel}</strong>`,
          department: `<span id="preview-dept-label">${escHtml(previewDepartment)}</span>`,
        })
        : t('shell.preview.banner', {
          company: `<strong id="preview-company-name">${companyLabel}</strong>`,
        })}</span>
      <a href="/" id="exit-preview">${t('shell.preview.exit')}</a>
    `;
    mainWrapper.appendChild(banner);
    banner.querySelector('#exit-preview')?.addEventListener('click', (e) => {
      e.preventDefault();
      window.location.href = '/';
    });
    if (previewCompanyId !== 'global') {
      api.get('/admin/companies').then(companies => {
        const match = (companies || []).find(c => String(c.id) === previewCompanyId || c.slug === previewCompanyId);
        if (match) {
          const nameEl = banner.querySelector('#preview-company-name');
          if (nameEl) nameEl.textContent = match.name;
        }
      }).catch(() => {});
    }
  }

  const toolbar = document.createElement('div');
  toolbar.className = 'global-toolbar';
  toolbar.style.cssText = 'display:flex;align-items:center;justify-content:flex-end;padding:0 var(--space-4);height:48px;border-bottom:1px solid var(--border-secondary);background:var(--surface-primary);gap:var(--space-2);flex-shrink:0;';

  if (isAdmin) {
    const previewMount = document.createElement('div');
    toolbar.appendChild(previewMount);
    renderPreviewLauncher(previewMount);
  }

  const langMount = document.createElement('span');
  toolbar.appendChild(langMount);
  renderLanguageSwitcher(langMount);

  const main = document.createElement('main');
  main.className = 'main-content';
  main.id = 'main-content';

  mainWrapper.appendChild(toolbar);
  mainWrapper.appendChild(main);

  layout.appendChild(mobileHeader);
  layout.appendChild(overlay);
  layout.appendChild(sidebar);
  layout.appendChild(mainWrapper);
  app.appendChild(layout);

  // AI Assistant widget (admins, as before)
  if (isAdmin) {
    const aiMount = document.createElement('div');
    app.appendChild(aiMount);
    renderAIAssistant(aiMount);
  }
}

// Refresh the `active` class on sidebar links for the current pathname.
// Exact match for '/' and '/admin' (otherwise '/admin' stays lit on every
// admin sub-page); prefix match for the rest.
function updateNavActive() {
  const currentPath = window.location.pathname;
  document.querySelectorAll('.sidebar .nav-item[data-path]').forEach((el) => {
    const path = el.getAttribute('data-path') || '';
    const isActive = (path === '/' || path === '/admin')
      ? currentPath === path
      : currentPath.startsWith(path);
    el.classList.toggle('active', isActive);
    if (isActive) el.setAttribute('aria-current', 'page');
    else el.removeAttribute('aria-current');
  });
}

// ── Init ─────────────────────────────────────────────────────────────
async function init() {
  initTheme();
  await loadBranding().catch(() => {});
  const hasSession = await auth.checkSession();

  // Resolve the language BEFORE anything renders — the catalogue for a
  // non-English locale is a lazy chunk, so awaiting here is what stops the
  // shell painting in English and visibly re-rendering a moment later.
  await initI18n(store.get('user')?.locale || null).catch(() => {});

  // Define user routes
  router
    .on('/', (params) => renderPage(pages.dashboard, params))
    .on('/documents', (params) => renderPage(pages.documents, params))
    .on('/module/:moduleId', (params) => renderPage(pages.module, params))
    .on('/login', (params) => renderPage(pages.login, params));

  // Define admin routes
  router
    .on('/admin', (params) => renderPage(pages.adminDashboard, params))
    .on('/admin/modules', (params) => renderPage(pages.adminModules, params))
    .on('/admin/modules/:id', (params) => renderPage(pages.adminModuleEditor, params))
    .on('/admin/users', (params) => renderPage(pages.adminUsers, params))
    .on('/admin/media', (params) => renderPage(pages.adminMedia, params))
    .on('/admin/reports', (params) => renderPage(pages.adminReports, params));

  // Auth guard
  router.before((path) => {
    // An AUTHENTICATED user must never be left on the login page. Waving
    // /login through unconditionally renders the login FORM inside the app
    // shell — the app looks signed in and signed out at once, and only a
    // manual reload clears it. Not an edge case: api.js bounces here on any
    // 401 while the httpOnly cookie usually survives, so the reload lands
    // authenticated; the SSO broker's ?next and plain bookmarks reach it too.
    // Fixed in isg-app-template 2026-07-18; back-propagated 2026-07-31.
    if (path === '/login' && store.get('isAuthenticated')) {
      router.navigate('/');
      return false;
    }
    if (path === '/login') return true;
    if (!store.get('isAuthenticated')) {
      router.navigate('/login');
      return false;
    }
    // Admin guard
    if (path.startsWith('/admin') && !isInductionAdmin()) {
      router.navigate('/');
      return false;
    }
    return true;
  });

  // Apply branding + refresh sidebar active state after every route change
  router.after(() => {
    setTimeout(() => applyBranding(), 50);
    updateNavActive();
  });

  if (!hasSession) {
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    const module = await pages.login();
    module.render(document.getElementById('main-content'));
    if (window.location.pathname !== '/login') {
      window.history.replaceState(null, '', '/login');
    }
    applyBranding();
  } else {
    renderAppShell();
    checkEnvBanner(); // beta/test banner (no-op on live)
    await router.resolve();
    applyBranding();
  }
}

// Register service worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' })
      .then((reg) => console.log('SW registered:', reg.scope))
      .catch((err) => console.warn('SW registration failed:', err));
  });
}

// Boot
init().catch((err) => {
  console.error('Failed to initialize app:', err);
  document.getElementById('app').innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;flex-direction:column;gap:1rem;">
      <p>${t('shell.bootFailed')}</p>
      <button class="btn btn-primary" onclick="location.reload()">${t('shell.reload')}</button>
    </div>
  `;
});
